import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, Download, Check, Clock, AlertTriangle, Plus, Users, Trash2, Upload,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { fechaCorta } from '../ui/documentos';
import EtiquetaRevision from '../ui/EtiquetaRevision';
import { Cargando } from '../ui/EstadoDePantalla';
import { etiquetaRol } from '../lib/roles';
import { useCapa } from '../ui/navegacionReact';

// LA FICHA DE UN DOCUMENTO · sus revisiones, quién debe leerlo, y quién
// lo ha leído ya. Es donde se contestan las tres preguntas del auditor.
//
// LA DECISIÓN DE DISEÑO QUE MÁS PESA AQUÍ: **aprobar pide confirmación y
// dice la consecuencia en una frase**. Aprobar no es guardar: vuelve
// obsoleta la revisión anterior y obliga a toda la lista de distribución
// a volver a leerse el documento. Un botón «Aprobar» a secas convierte
// eso en un accidente de un clic.

function ConfirmarAprobar({ revision, cuantos, onSi, onNo, ocupado }) {
  return (
    <div role="dialog" aria-modal="true"
         className="border border-amber-300 bg-amber-50 rounded-xl p-4 space-y-3">
      <p className="font-semibold text-gray-900">
        ¿Aprobar la revisión {revision.numero}?
      </p>
      {/* LA CONSECUENCIA, EN UNA FRASE (Doctrina 2, regla 7). */}
      <p className="text-sm text-gray-700">
        A partir de ese momento será la única versión válida. La revisión que
        esté vigente ahora quedará <strong>obsoleta</strong>, y{' '}
        {cuantos > 0
          ? <><strong>{cuantos} persona(s)</strong> tendrán que volver a leerla</>
          : <>nadie tendrá que releerla todavía, porque aún no hay nadie en la
             lista de quién debe leerlo</>}.
      </p>
      <div className="flex gap-2">
        <button onClick={onSi} disabled={ocupado}
                className="bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
          {ocupado ? 'Aprobando…' : 'Sí, aprobar'}
        </button>
        <button onClick={onNo}
                className="border border-gray-300 bg-white text-gray-800 text-sm font-medium px-4 py-2 rounded-lg">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function SubirRevision({ idDocumento, onListo, onCancelar }) {
  const [numero, setNumero] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [cambio, setCambio] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const falta = !numero.trim() ? 'el número de la revisión'
    : cambio.trim().length < 3 ? 'qué cambió respecto a la anterior'
    : !archivo ? 'el archivo del documento'
    : null;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setOcupado(true);
    try {
      // El archivo primero: si falla, no queda una revisión sin papel.
      const subido = await api.subir(
        '/calidad/adjuntos?proposito=DOCUMENTO', archivo);

      const d = await api.post(`/documentos/${idDocumento}/revisiones`, {
        numero, fecha, descripcion_cambio: cambio, id_archivo: subido.id,
      });
      onListo(d);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
      <h4 className="font-semibold text-gray-900">Subir una revisión nueva</h4>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Número</span>
          <input value={numero} onChange={(e) => setNumero(e.target.value)}
                 placeholder="1"
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <span className="block text-xs text-gray-500 mt-1">
            El que use tu planta: «0», «1», «A» o «2.1».
          </span>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Fecha</span>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Qué cambió</span>
        <textarea value={cambio} onChange={(e) => setCambio(e.target.value)}
                  rows={3} placeholder="Se añadió la verificación de dureza en el paso 4."
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <span className="block text-xs text-gray-500 mt-1">
          Es lo que un auditor lee para entender por qué hubo una revisión nueva.
        </span>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">El documento</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png"
               onChange={(e) => setArchivo(e.target.files[0] || null)}
               className="mt-1 w-full text-sm" />
        <span className="block text-xs text-gray-500 mt-1">
          PDF o imagen, hasta 10 MB.
        </span>
      </label>

      {error ? (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!!falta || ocupado}
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
          <Upload size={16} aria-hidden="true" />
          {ocupado ? 'Subiendo…' : 'Guardar como borrador'}
        </button>
        <button type="button" onClick={onCancelar}
                className="text-sm text-gray-600">Cancelar</button>
        {falta ? <span className="text-xs text-gray-500">Falta {falta}.</span> : null}
      </div>
      <p className="text-xs text-gray-500">
        Guardar no la publica: hasta que alguien la apruebe, la vigente sigue
        siendo la de ahora.
      </p>
    </form>
  );
}

export default function FichaDocumento({ id, onVolver, puedeEditar, puedeAprobar }) {
  const [d, setD] = useState(null);
  const [matriz, setMatriz] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  useCapa(!!confirmar, () => setConfirmar(null));  // la pregunta «¿Aprobar?»: atrás la retira
  const [ocupado, setOcupado] = useState(false);
  const [bajando, setBajando] = useState(null);

  const cargar = useCallback(() => {
    Promise.all([
      api.get(`/documentos/${id}`),
      api.get(`/documentos/${id}/matriz`),
    ])
      .then(([ficha, m]) => { setD(ficha); setMatriz(m); })
      .catch((e) => setError(e.message));
  }, [id]);

  useEffect(cargar, [cargar]);

  async function descargar(r) {
    setError(null);
    setBajando(r.id);
    try {
      const blob = await api.bajar(`/documentos/revision/${r.id}/archivo`);
      const sufijo = r.estado === 'VIGENTE' ? '' : '-OBSOLETO';
      guardarArchivo(blob, `${d.documento.codigo}-rev${r.numero}${sufijo}.pdf`);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo descargar.');
    } finally {
      setBajando(null);
    }
  }

  async function aprobar(rev) {
    setOcupado(true);
    setError(null);
    try {
      const r = await api.post(`/documentos/revision/${rev.id}/aprobar`, {});
      setMsg(r.mensaje);
      setConfirmar(null);
      cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo aprobar.');
    } finally {
      setOcupado(false);
    }
  }

  async function quitarDe(idDist) {
    setError(null);
    try {
      const r = await api.del(`/documentos/distribucion/${idDist}`);
      setMsg(r.mensaje);
      cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo quitar.');
    }
  }

  if (error && !d) {
    return (
      <div className="space-y-3">
        <button onClick={onVolver} className="inline-flex items-center gap-1 text-sm text-gray-600">
          <ArrowLeft size={16} aria-hidden="true" /> Volver
        </button>
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
          {error}
        </div>
      </div>
    );
  }
  if (!d) return <Cargando filas={4} />;

  const doc = d.documento;

  return (
    <div className="space-y-5">
      <button onClick={onVolver} className="inline-flex items-center gap-1 text-sm text-gray-600">
        <ArrowLeft size={16} aria-hidden="true" /> Volver a documentos
      </button>

      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-semibold text-gray-900 font-mono">{doc.codigo}</h2>
          {doc.estado === 'RETIRADO' ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
              Retirado
            </span>
          ) : null}
        </div>
        <p className="text-gray-900">{doc.titulo}</p>
        <p className="text-sm text-gray-500 mt-0.5">
          {doc.tipo_nombre}
          {doc.proceso ? ` · ${doc.proceso}` : ''}
          {doc.responsable ? ` · responsable: ${doc.responsable}` : ''}
        </p>
        {doc.fecha_proxima_revision ? (
          <p className="text-sm text-gray-500">
            Toca revisarlo el {fechaCorta(doc.fecha_proxima_revision)}.
          </p>
        ) : null}
      </div>

      {msg ? (
        <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {msg}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </p>
      ) : null}

      {/* ---------------- Revisiones ---------------- */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Revisiones</h3>
          {puedeEditar && !subiendo ? (
            <button onClick={() => setSubiendo(true)}
                    className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-800 text-sm font-medium px-3 py-1.5 rounded-lg">
              <Plus size={15} aria-hidden="true" /> Subir revisión
            </button>
          ) : null}
        </div>

        {subiendo ? (
          <SubirRevision idDocumento={id} onCancelar={() => setSubiendo(false)}
                         onListo={(r) => { setSubiendo(false); setMsg(r.mensaje); cargar(); }} />
        ) : null}

        {(d.revisiones || []).length === 0 && !subiendo ? (
          <div className="border border-dashed border-gray-300 rounded-xl p-5 text-center">
            <p className="font-medium text-gray-900">Sin revisiones</p>
            <p className="mt-1 text-sm text-gray-500">
              Mientras no haya una aprobada, este documento no lo ve nadie y no
              se le puede pedir a nadie que lo lea.
            </p>
          </div>
        ) : null}

        {(d.revisiones || []).map((r) => (
          <div key={r.id} className="border border-gray-200 rounded-xl p-3 bg-white">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-900">Revisión {r.numero}</span>
              <EtiquetaRevision estado={r.estado} />
              <span className="text-xs text-gray-500">del {fechaCorta(r.fecha)}</span>
            </div>
            <p className="mt-1 text-sm text-gray-700">{r.descripcion_cambio}</p>
            <p className="mt-1 text-xs text-gray-500">
              {r.aprobo ? `Aprobó ${r.aprobo}` : 'Sin aprobar'}
              {r.elaboro ? ` · elaboró ${r.elaboro}` : ''}
              {r.reviso ? ` · revisó ${r.reviso}` : ''}
              {` · ${r.acuses} acuse(s) de lectura`}
            </p>

            <div className="mt-2 flex flex-wrap gap-2">
              {/* NO ES UN <a href>: la sesión va en la cabecera
                  Authorization y un enlace normal no la manda. */}
              {r.archivo ? (
                <button onClick={() => descargar(r)}
                        disabled={bajando === r.id}
                        className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-800 text-sm px-3 py-1.5 rounded-lg disabled:opacity-50">
                  <Download size={15} aria-hidden="true" />
                  {bajando === r.id ? 'Descargando…' : 'Descargar'}
                  {r.estado !== 'VIGENTE' ? ' (marcado OBSOLETO)' : ''}
                </button>
              ) : null}
              {puedeAprobar && r.estado === 'BORRADOR' ? (
                <button onClick={() => setConfirmar(r)}
                        className="inline-flex items-center gap-1.5 bg-amber-600 text-white text-sm px-3 py-1.5 rounded-lg">
                  <Check size={15} aria-hidden="true" /> Aprobar
                </button>
              ) : null}
            </div>

            {confirmar && confirmar.id === r.id ? (
              <div className="mt-3">
                <ConfirmarAprobar revision={r} cuantos={matriz ? matriz.total : 0}
                                  ocupado={ocupado}
                                  onSi={() => aprobar(r)} onNo={() => setConfirmar(null)} />
              </div>
            ) : null}
          </div>
        ))}
      </section>

      {/* ---------------- La matriz de lectura ---------------- */}
      <section className="space-y-2">
        <h3 className="font-semibold text-gray-900">Quién lo ha leído</h3>
        {matriz && matriz.revision ? (
          <>
            <p className="text-sm text-gray-500">
              Sobre la revisión {matriz.revision}: {matriz.leidos} de {matriz.total}{' '}
              {matriz.pendientes > 0
                ? <span className="text-amber-800">({matriz.pendientes} sin leer)</span>
                : <span className="text-green-800">(todos al día)</span>}
            </p>
            {matriz.filas.length === 0 ? (
              <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl p-4">
                {matriz.mensaje_vacio}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs">
                      <th className="py-1">Persona</th>
                      <th className="py-1">Puesto</th>
                      <th className="py-1">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matriz.filas.map((f) => (
                      <tr key={f.id_usuario} className="border-t border-gray-100">
                        <td className="py-1.5">{f.persona}</td>
                        <td className="py-1.5 text-gray-500">{etiquetaRol(f.rol)}</td>
                        <td className="py-1.5">
                          {f.leido ? (
                            <span className="inline-flex items-center gap-1 text-green-800">
                              <Check size={14} aria-hidden="true" /> Leído
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-800">
                              <Clock size={14} aria-hidden="true" /> Sin leer
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl p-4">
            {matriz ? matriz.mensaje_vacio : 'Cargando…'}
          </p>
        )}
      </section>

      {/* ---------------- Quién debe leerlo ---------------- */}
      {puedeEditar ? (
        <section className="space-y-2">
          <h3 className="font-semibold text-gray-900">Quién debe leerlo</h3>
          {(d.distribucion || []).length === 0 ? (
            <p className="text-sm text-gray-500">
              Nadie todavía. Mientras esté así, aprobar una revisión no le dará
              trabajo a nadie y la matriz de arriba estará vacía.
            </p>
          ) : (
            <ul className="space-y-1">
              {(d.distribucion || []).map((x) => (
                <li key={x.id} className="flex items-center justify-between text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white">
                  <span className="inline-flex items-center gap-2">
                    <Users size={15} className="text-gray-400" aria-hidden="true" />
                    {x.persona || `Todos los de ${etiquetaRol(x.rol)}`}
                  </span>
                  <button onClick={() => quitarDe(x.id)}
                          className="text-gray-400 hover:text-red-600"
                          aria-label={`Quitar a ${x.persona || etiquetaRol(x.rol)}`}>
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
